import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Globe, Phone } from "lucide-react";
import confetti from "canvas-confetti";
import analyticsService from "../lib/AnalyticsService";
import { useInteractionTracking } from "../hooks/useAnalytics";

/**
 * Returns true if current time is Mon–Fri 8am–9pm Eastern.
 */
/**
 * Returns true if current time is Mon–Fri 8am–9pm Eastern.
 */
const isWithinVATHours = () => {
  const now = new Date();
  const eastern = new Date(
    now.toLocaleString("en-US", { timeZone: "America/New_York" })
  );
  const day = eastern.getDay(); // 0=Sun, 6=Sat
  const hour = eastern.getHours();
  return day >= 1 && day <= 5 && hour >= 8 && hour < 21;
};

const ViuChoiceScreen = ({ buyerData, onDigitalQuote, onTalkToAgent }) => {
  const { trackButtonClick } = useInteractionTracking();
  const headerRef = useRef(null);
  const vatHours = isWithinVATHours();
  const [clicked, setClicked] = useState(null);

  // Fire confetti on mount
  useEffect(() => {
    if (!headerRef.current) return;
    const rect = headerRef.current.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;
    confetti({
      particleCount: 100,
      spread: 80,
      origin: { x, y },
      colors: ["#7B61FF", "#D4F6C9", "#CEC5FF", "#FFD700", "#A1CE90"],
    });
  }, []);

  useEffect(() => {
    analyticsService.trackPageView("viu_choice_screen", {
      buyer: buyerData?.name,
      vat_hours: vatHours,
    });
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-4" ref={headerRef}>
        {buyerData?.logo && (
          <div className="flex justify-center">
            <div className="p-4 rounded-lg">
              <img
                src={buyerData.logo}
                alt={buyerData.name}
                className="h-16 w-auto object-contain"
              />
            </div>
          </div>
        )}
        <h1 className="text-xl font-bold">
          Insurance has never been this easy
        </h1>
        <p className="text-darkGrey">
          Compare quotes from 50+ A-rated carriers online or speak with a
          licensed VIU by HUB Advisor, whichever works best for you.
        </p>
        {!vatHours && (
          <p className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3">
            Our advisors are currently unavailable. Request a callback below and
            we'll be in touch — Monday through Friday, 8am to 9pm ET.
          </p>
        )}
      </div>

      {/* Option 1: Speak to an Advisor / Schedule a Callback */}
      <Card className="border-2 border-[#7B61FF] bg-[#F5F2FF] rounded-[20px]">
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <Phone className="w-12 h-12 text-[#7B61FF] mx-auto" />
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">
                {vatHours ? "Speak to an advisor" : "Schedule a callback"}
              </h2>
              <p className="text-gray-600 text-sm">
                {vatHours
                  ? "Get a call from a VIU by HUB Advisor within 10 minutes to find the right coverage for your needs."
                  : "Leave your number and a licensed advisor will call you during business hours."}
              </p>
            </div>
            <button
              disabled={!!clicked}
              onClick={() => {
                if (clicked) return;
                setClicked("agent");
                trackButtonClick("viu-talk-to-agent", {
                  step_name: "viu_choice_screen",
                  vat_hours: vatHours,
                });
                onTalkToAgent(buyerData);
              }}
              className="w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Phone className="w-5 h-5" />
              <span>{clicked === "agent" ? "Requesting…" : "Request a call"}</span>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Option 2: Compare Quotes Online */}
      <Card className="border-2 border-gray-200 rounded-[20px]">
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <Globe className="w-12 h-12 text-gray-500 mx-auto" />
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">
                Compare quotes online
              </h2>
              <p className="text-gray-600 text-sm">
                Compare quotes from 50+ A-rated carriers now.
              </p>
            </div>
            <button
              disabled={!!clicked}
              onClick={() => {
                if (clicked) return;
                setClicked("quote");
                trackButtonClick("viu-digital-quote", {
                  step_name: "viu_choice_screen",
                });
                onDigitalQuote(buyerData);
              }}
              className="w-full flex items-center justify-center gap-2 uppercase font-bold border-2 border-[#7B61FF] text-[#7B61FF] px-4 py-3 rounded-[10px] hover:bg-[#F5F2FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Globe className="w-5 h-5" />
              <span>{clicked === "quote" ? "Loading…" : "Get quotes"}</span>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Consent disclosure */}
      <p className="text-sm text-gray-600 text-center">
        By clicking "Get Quotes" or "Request a Call", you agree: (1) to your profile information being sent to VIU by HUB for use according to its{" "}
        <a href="https://www.viubyhub.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a>
        , and (2) that VIU by HUB may contact you via email or telephone call about your insurance needs. You can opt-out of marketing communications at any time.
      </p>
    </div>
  );
};

export default ViuChoiceScreen;
